import React from 'react';
import { Loader2 } from 'lucide-react';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: React.ReactNode;
  helperLoading?: boolean;
  append?: React.ReactNode;
  size?: 'md' | 'sm';
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      helperLoading = false,
      append,
      size = 'md',
      className = '',
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      md: 'py-2',
      sm: 'py-1.5 text-base',
    };

    return (
      <div className="space-y-1">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <div className={append ? 'flex' : undefined}>
          <input
            ref={ref}
            className={`
              w-full px-3 border border-gray-300 rounded-md shadow-sm
              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
              disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
              ${sizeClasses[size]}
              ${append ? 'rounded-r-none border-r-0' : ''}
              ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
              ${className}
            `}
            {...props}
          />
          {append && (
            <span
              className={`
                inline-flex items-center px-3 border border-gray-300 rounded-r-md
                bg-gray-50 text-gray-700 text-sm
                ${error ? 'border-red-300' : ''}
              `}
            >
              {append}
            </span>
          )}
        </div>
        {error && (
          <p className="text-sm text-red-600">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="text-sm text-gray-500 flex items-center gap-1">
            {helperLoading && (
              <Loader2 className="w-3 h-3 animate-spin" />
            )}
            <span>{helperText}</span>
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
